import { OWDataType } from "@multiweb/types/contracts";
import { AppContext } from "../../App";
import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { BigNumberProxy } from "../../Global/Base/DataTypes/BigNumber";
import { OWDate } from "../../Global/Base/DataTypes/OWDate";
import { OWTime } from "../../Global/Base/DataTypes/OWTime";
import { DateUtils } from "../../Global/Base/Utils/DateUtils";
import { EnumUtils } from "../../Global/Base/Utils/EnumUtils";
import { isVersionOlderOrEqual } from "../../Global/Base/Utils/versionUtils";
import { EInvalidValueException } from "../Basic/OWExceptions";
import { ApplicationStorage, AppStorageItemType } from "./ApplicationStorage";
import { BasicDataType, IDataTypeDescription, IDataTypeSerialization } from "./Cache/ClassDescriptionCache/Interfaces";
import { Enum } from "./DataTypes/Enums";
import { Flags } from "./DataTypes/Flags";
import { IOWObject } from "./DataTypes/IOWObject";
import { LocalizedText } from "./DataTypes/LocalizedText";
import { OWDocumentDescription } from "./DataTypes/OWDocumentDescription";
import { OWObjectList } from "./DataTypes/OWObjectList";
import { OWVariant } from "./DataTypes/OWVariant";
import { OWManager } from "./OWManager";
import { SerializerParams } from "./types";

export class SerializeFuncs {
    /**
     * Prefix typu atributu pro určení typu jako enum. Za prefixem se nachází název konkrétního enumu.
     * @private
     */
    private static readonly _ENUM_ATTRIBUTE_PREFIX = "Enum|";

    /**
     * Prefix typu atributu pro určení typu jako OW objektu. Za prefixem se nachází název třídy.
     * @private
     */
    private static readonly _OW_OBJECT_ATTRIBUTE_PREFIX = "OWObject|";

    public static deserializeBigNumber(value: any): any {
        if (typeof value === "number")
            return value;
        if (value == null)
            return null;
        return new BigNumberProxy(value);
    }

    public static deserializeNumber(value: any): any {
        if (typeof value === "number")
            return value;
        return JSON.parse(value);
    }

    public static deserializeBoolean(value: any): any {
        if (typeof value === "boolean")
            return value;
        return JSON.parse(value);
    }

    public static deserializeFlags(value: any): any {
        return value ? Flags.get(value) : null;
    }

    public static deserializeColor(value: any): any {
        if (value) {
            return `#${value}`; // `
        }

        return null;
    }

    public static deserializeObject(constructor: new(value: string) => any, value: string): any {
        if (value === null || value === undefined || value === "")
            return null;
        return new constructor(value);
    }

    public static deserializeDate(value: string): any {
        return SerializeFuncs.deserializeObject(OWDate, value);
    }

    public static deserializeTime(value: string): any {
        return SerializeFuncs.deserializeObject(OWTime, value);
    }

    public static deserializeDateTime(value: string): any {
        return DateUtils.fromXsdDateTime(value);
    }

    private static _serializeStringAndNumberPimitives(value: string | number): number | string {
        if (typeof value === "number") {
            return SerializeFuncs.serializeNumber(value);
        } else if (typeof value === "string") {
            return SerializeFuncs.serializeString(value);
        } else {
            return 0;
        }
    }

    //  Serializér se vybírá podle attrDescription a v určitých případech (např. nedočtený objekt) může být value pouze samotné id/isntanceId (nikoliv celý objekt)
    //  proto value může být i typu string | number
    private static _serializeOWObject(value: IOWObject | string | number, useIdInsteadOfInstanceId: boolean): number | string {
        if (value instanceof IOWObject) {
            return SerializeFuncs.serializeString(useIdInsteadOfInstanceId ? value.id : value.instanceID);
        } else {
            return SerializeFuncs._serializeStringAndNumberPimitives(value);
        }
    }

    public static serializeOWObjectWithInstance(value: IOWObject | string | number): number | string {
        return SerializeFuncs._serializeOWObject(value, false);
    }

    public static serializeOWObjectWithId(value: IOWObject | string | number): number | string {
        return SerializeFuncs._serializeOWObject(value, true);
    }

    public static serializeOWObjectList(value: any): any {
        if (value instanceof OWObjectList)
            return value.toJSON();
        return 0;
    }

    public static deserializeOWObjectList(value: any): any {
        if (value == null)
            return null;
        return new OWObjectList(value);
    }

    public static serializeNumber(value: any): string | number {
        try {
            if (typeof value === "number")
                return value;
            return value.toString();
        } catch (e) {
            throw new EInvalidValueException();
        }
    }

    public static serializeBool(value: any): boolean {
        if (typeof value === "boolean")
            return value;
        throw new EInvalidValueException();
    }

    public static serializeColor(value: any): string {
        return value.toString().slice(1); // bez #
    }

    public static serializeDateTime(value: any): string {
        if (value instanceof Date)
            return DateUtils.toXsdDateTime(value);
        throw new EInvalidValueException();
    }

    public static serializeDate(value: any): string {
        if (value instanceof OWDate)
            return value.toJSON();
        if (value instanceof Date)
            return (new OWDate(value)).toJSON();
        throw new EInvalidValueException();
    }

    public static serializeTime(value: any): string {
        if (value instanceof OWTime)
            return value.toJSON();
        if (value instanceof Date)
            return (new OWTime(value)).toJSON();
        throw new EInvalidValueException();
    }

    public static serializeJSON(value: any): any {
        if (value.toJSON)
            return value.toJSON();
        throw new EInvalidValueException();
    }

    public static serializeString(value: any): string {
        return value.toString();
    }

    public static serializeFlags(value: any): string {
        //var flag = Flags.fNone.GetType().GetValue(value.toString());
        //if (flag) return flag;
        //else return Flags.fNone;
        return value.toString();
    }

    public static serializeOWDocument(value: any): string {
        if (value instanceof OWDocumentDescription)
            return value.value;
        return "";
    }

    public static deserializeOWDocument(value: any, instanceId: string, attributeName: string): OWDocumentDescription {
        if (value instanceof OWDocumentDescription)
            return value;
        return new OWDocumentDescription(instanceId, attributeName, value);
    }

    public static serializeStrNum(value: any): any {
        if (value == null || typeof value === "string" || typeof value === "number")
            return value;

        return value.toString();
    }

    public static deserializeStrNum(value: any): any {
        if (value == null || value instanceof OWVariant)
            return value;
        return new OWVariant(value);
    }

    public static serializeLocalizedText(value: any, oldValue?: any): string {
        let locText = value;
        // pokud nastavuji string do localizedTextu, musím jej mergovat s aktuální hodnotou, jinak mohu ztratit překlady
        if (oldValue && typeof oldValue === "object" && typeof value === "string") {
            locText = oldValue as LocalizedText;
            locText.translations[ApplicationStorage.get(AppStorageItemType.language)] = value;
        }
        if (typeof locText === "object")
            return JSON.stringify(locText.translations);
        if (locText == null)
            return null;
        return locText.toString();
    }

    public static deserializeLocalizedText(value: any): LocalizedText {
        if (value) {
            if (isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "21.01")) value = JSON.parse(value);
            return new LocalizedText(value);
        }
        return null;
    }

    public static unserializable(_value: any): void {
        throw new EInvalidValueException();
    }

    public static deserializeUnknown(_value: string): string {
        return "Unknown Data Type!";
    }

    public static serializeBinary(value: any): string {
        if (value == null)
            return value;
        return DataConverter.bufferToBase64(value);
    }

    public static deserializeBinary(value: any): Uint8Array {
        if (value == null)
            return value;
        return DataConverter.base64ToArray(value);
    }

    /**
     * konvertuje retezec popisujici datovy typ na serveru do interface IDataTypeDescription
     */
    public static async getTypeSerializers(type: OWDataType | string, enumName?: string, params?: SerializerParams): Promise<IDataTypeSerialization> {
        if (type?.startsWith("Enum|") || type === OWDataType.enum || enumName) {
            enumName = enumName ? enumName : type.substr(5);
            const enumType = await OWManager.getEnum(enumName);
            return {
                deserializer: (value: string) => {
                    if (value)
                        return enumType.getValue(value);
                    return null;
                },
                serializer: (value: Enum) => Number(value),
            };
        }

        if (type?.indexOf("OWObject|") === 0)
            return { serializer: SerializeFuncs.serializeOWObjectWithInstance };

        const dataType = typeof type === "string" ? EnumUtils.getEnumValueFromName(OWDataType, type) : type;

        switch (dataType) {
            case OWDataType.byte:
            case OWDataType.short:
            case OWDataType.integer:
            case OWDataType.single:
            case OWDataType.double:
                return { deserializer: SerializeFuncs.deserializeNumber, serializer: SerializeFuncs.serializeNumber };

            case OWDataType.bcd:
            case OWDataType.money:
            case OWDataType.decimal:
            case OWDataType.int64:
                return { deserializer: SerializeFuncs.deserializeBigNumber, serializer: SerializeFuncs.serializeNumber };

            case OWDataType.date:
                return { deserializer: SerializeFuncs.deserializeDate, serializer: SerializeFuncs.serializeDate };
            case OWDataType.time:
                return { deserializer: SerializeFuncs.deserializeTime, serializer: SerializeFuncs.serializeTime };
            case OWDataType.dateTime:
                return { deserializer: SerializeFuncs.deserializeDateTime, serializer: SerializeFuncs.serializeDateTime };

            case OWDataType.string:
            case OWDataType.text:
            case OWDataType.htmlText:
            case OWDataType.timeSpan:
                return { serializer: SerializeFuncs.serializeString };

            case OWDataType.strNum:
                return { deserializer: SerializeFuncs.deserializeStrNum, serializer: SerializeFuncs.serializeStrNum };

            case OWDataType.localizedText:
                return { deserializer: SerializeFuncs.deserializeLocalizedText, serializer: SerializeFuncs.serializeLocalizedText };

            case OWDataType.flags:
                return { deserializer: SerializeFuncs.deserializeFlags, serializer: SerializeFuncs.serializeFlags };

            case OWDataType.boolean:
                return { deserializer: SerializeFuncs.deserializeBoolean, serializer: SerializeFuncs.serializeBool };

            case OWDataType.color:
                return { deserializer: SerializeFuncs.deserializeColor, serializer: SerializeFuncs.serializeColor };

            case OWDataType.OWObject:
                return { serializer:  (params && params.serializeWithId ? SerializeFuncs.serializeOWObjectWithId : SerializeFuncs.serializeOWObjectWithInstance) };

            case OWDataType.document:
                return { serializer: SerializeFuncs.serializeOWDocument };

            case OWDataType.binary:
                return { deserializer: SerializeFuncs.deserializeBinary, serializer: SerializeFuncs.serializeBinary };

            case OWDataType.OWObjectList:
                return { deserializer: SerializeFuncs.deserializeOWObjectList, serializer: SerializeFuncs.serializeOWObjectList };

            default:
                return { deserializer: SerializeFuncs.deserializeUnknown, serializer: SerializeFuncs.unserializable };
        }
    }

    /**
     * Získá specifickou část popisu typu atributu pro atributy typu enum. Pomocná funkce pro {@link getTypeDescription}.
     * @param type Typ atributu. Může být složený z prefixu {@link _ENUM_ATTRIBUTE_PREFIX} a názvu enumu.
     * @param attrName Název atributu.
     * @param isCompositeEnumType Příznak, zda je název enumu součástí názvu atributu. Slouží pouze k odstranění duplicitní kontroly v souvislosti s rozhodováním ve funkci {@link getTypeDescription}.
     * @param enumName Název enumu. Pokud je zadaný, má přednost před případným názvem enumu v názvu atributu.
     * @private
     * @throws Vyhazuje chybu v případě, kdy název enumu není zadaný jako parametr enumName a ani se nevyskytuje v typu atributu.
     */
    private static _getSpecificTypeDescriptionForEnum(type: OWDataType | string, attrName: string, isCompositeEnumType: boolean, enumName?: string): Pick<IDataTypeDescription, "basicType" | "serverType"> {
        let serverType;
        if (enumName) {
            serverType = enumName;
        } else if (isCompositeEnumType && type.length > SerializeFuncs._ENUM_ATTRIBUTE_PREFIX.length) {
            serverType = type.substr(SerializeFuncs._ENUM_ATTRIBUTE_PREFIX.length);
        } else {
            throw new Error(`Cannot get type description for attribute ${attrName} of type ${type}, because enumName is not specified and type does not contain enum name.`);
        }

        return {
            basicType: BasicDataType.Enum,
            serverType,
        };
    }

    /**
     * Získá specifickou část popisu typu atributu pro běžné (bez enumu a OWObject) atributy typu OWDataType. V podstatě převádí {@link OWDataType} na {@link BasicDataType}. Pomocná funkce pro {@link getTypeDescription}.
     * @param type Typ atributu.
     * @private
     */
    private static _getSpecificTypeDescriptionForOWDataType(type: OWDataType | string): Partial<IDataTypeDescription> {
        const dataType = typeof type === "string" ? EnumUtils.getEnumValueFromName(OWDataType, type) : type;

        switch (dataType) {
            case OWDataType.byte:
            case OWDataType.short:
            case OWDataType.integer:
            case OWDataType.single:
            case OWDataType.double:
                return { basicType: BasicDataType.BasicNumber };
            case OWDataType.bcd:
            case OWDataType.money:
            case OWDataType.decimal:
            case OWDataType.int64:
                return { basicType: BasicDataType.BigNumber };

            case OWDataType.date:
            case OWDataType.time:
            case OWDataType.dateTime:
                return { basicType: BasicDataType.DateTime };

            case OWDataType.string:
            case OWDataType.text:
            case OWDataType.htmlText:
                return { basicType: BasicDataType.String };

            case OWDataType.strNum:
                return { basicType: BasicDataType.StrNum };

            case OWDataType.localizedText:
                return { basicType: BasicDataType.String, defaultValue: "" };

            case OWDataType.flags:
                return { basicType: BasicDataType.Enum };
            case OWDataType.boolean:
                return { basicType: BasicDataType.Boolean };
            case OWDataType.color:
                return { basicType: BasicDataType.Color };

            case OWDataType.OWObject:
                return { basicType: BasicDataType.OWObject };

            case OWDataType.binary:
                return { basicType: BasicDataType.Binary };

            case OWDataType.document:
                return { basicType: BasicDataType.Document };

            case OWDataType.OWObjectList:
                return { basicType: BasicDataType.OWObjectList };

            default:
                return { basicType: BasicDataType.Unknown };
        }
    }

    /**
     * Získá popis typu atributu ve formátu {@link IDataTypeDescription}. Pokud není typ atributu rozpoznán, vrací v {@link IDataTypeDescription#basicType} hodnotu {@link BasicDataType#Unknown}.
     * @param type Typ atributu. Může být složený z prefixů pro specifikaci typu enumu {@link _ENUM_ATTRIBUTE_PREFIX} nebo OW objektu {@link _OW_OBJECT_ATTRIBUTE_PREFIX} a samotného názvu.
     * @param attrName Název atributu.
     * @param enumName Název enumu. Pokud je zadaný, má přednost před případným názvem enumu v názvu atributu. Zároveň se v takovém případě ignoruje hodnota parametru type a atribut se vždy zpracuje jako enum.
     * @throws Vyhazuje chybu v případě, kdy je atribut rozpoznán jako enum, ale název enumu není zadaný jako parametr enumName a ani se nevyskytuje v typu atributu.
     */
    public static getTypeDescription(type: OWDataType | string, attrName: string, enumName?: string): IDataTypeDescription {
        const defaultTypeDescription: IDataTypeDescription = { basicType: BasicDataType.Unknown, defaultValue: null, attrName: attrName };
        let specificTypeDescription: Partial<IDataTypeDescription>;

        // enumy
        const isCompositeEnumType = type?.startsWith(SerializeFuncs._ENUM_ATTRIBUTE_PREFIX);
        if (isCompositeEnumType || type === OWDataType.enum || enumName) {
            specificTypeDescription = SerializeFuncs._getSpecificTypeDescriptionForEnum(type, attrName, isCompositeEnumType, enumName);

        // OWObject
        } else if (type?.startsWith(SerializeFuncs._OW_OBJECT_ATTRIBUTE_PREFIX)) {
            specificTypeDescription = { basicType: BasicDataType.OWObject, defaultValue: 0 };

        // ostatní běžné typy
        } else {
            specificTypeDescription = SerializeFuncs._getSpecificTypeDescriptionForOWDataType(type);
        }

        return {
            ...defaultTypeDescription,
            ...specificTypeDescription,
        };
    }
}
